import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { TokenManager } from './tokenManager';
import { 
  getMsalInstanceSafely, 
  getAccountsSafely, 
  waitForMsalInitialization,
  isMsalReady 
} from './msalSetup';

// Enhanced error types
export interface AuthenticationError {
  status: 401;
  data: {
    message: string;
    code?: string;
  };
}

export interface ApiError {
  status: number;
  data: {
    message: string;
    details?: string;
  };
}

export interface ErrorResponse {
  message?: string;
  details?: string;
  [key: string]: unknown;
}

// Configuration for the base query
export interface BaseQueryConfig {
  baseUrl: string;
  tokenType?: 'frontend' | 'backend' | 'ad';
  clientId?: string;
  customHeaders?: Record<string, string>;
  onAuthenticationFailure?: () => void;
  waitForMsal?: boolean; // Whether to wait for MSAL initialization
  msalTimeoutMs?: number; // Timeout for MSAL initialization
  requiresAuth?: boolean; // Whether authentication is required
  // RTK Query endpoint names (as defined in createApi's `endpoints`) that should NOT trigger
  // the global forbidden-page redirect on a 403. Use this for actions where a 403 is an
  // expected, resource-specific business response (e.g. "you can't delete this particular
  // user") rather than "you have no business being in this app/section at all" — the caller
  // still receives the error normally and can show it inline.
  skipForbiddenRedirectForEndpoints?: string[];
}

// Default authentication failure handler
const defaultAuthFailureHandler = () => {
  console.log('[defaultAuthFailureHandler] Authentication failure detected, clearing tokens and redirecting');
  TokenManager.clearAll();
  
  if (typeof window !== 'undefined') {
    console.log('[defaultAuthFailureHandler] Redirecting to home page');
    window.location.href = '/';
  }
};

// Default forbidden handler for access-denied responses
const defaultForbiddenHandler = () => {
  if (typeof window !== 'undefined' && window.location.pathname !== '/unauthorized') {
    window.location.href = '/unauthorized';
  }
};

// Get token based on type (synchronous)
const getTokenByType = (tokenType: 'frontend' | 'backend' | 'ad' = 'backend'): string | null => {
  console.log(`[getTokenByType] Requesting token of type: ${tokenType}`);
  
  let token: string | null = null;
  switch (tokenType) {
    case 'frontend':
      token = TokenManager.getFrontendToken();
      break;
    case 'backend':
      token = TokenManager.getBackendToken();
      break;
    case 'ad':
      token = TokenManager.getAdToken();
      break;
    default:
      token = TokenManager.getBackendToken();
  }
  
  console.log(`[getTokenByType] Token retrieved for ${tokenType}:`, token ? `${token.substring(0, 20)}...` : 'null');
  return token;
};

// Check if any tokens are missing and initialize them
const initializeTokensIfMissing = async (
  msalInstance?: IPublicClientApplication,
  account?: AccountInfo,
  clientId?: string
): Promise<void> => {
  console.log('[initializeTokensIfMissing] Checking if tokens need initialization');
  
  // Use global MSAL state if parameters not provided
  const instance = msalInstance || getMsalInstanceSafely();
  const accounts = account ? [account] : getAccountsSafely();
  const primaryAccount = accounts[0];
  
  if (!instance || !primaryAccount) {
    console.warn('[initializeTokensIfMissing] No MSAL instance or account available');
    return;
  }
  
  const frontendToken = TokenManager.getFrontendToken();
  const backendToken = TokenManager.getBackendToken();
  const adToken = TokenManager.getAdToken();
  
  // If all tokens are missing, this might be a fresh session
  if (!frontendToken && !backendToken && (!clientId || !adToken)) {
    console.log('[initializeTokensIfMissing] No tokens found, initializing all tokens...');
    try {
      await TokenManager.initializeTokens(instance, primaryAccount, clientId);
      console.log('[initializeTokensIfMissing] All tokens initialized successfully');
    } catch (error) {
      console.log('[initializeTokensIfMissing] Failed to initialize tokens:', error);
    }
  } else {
    console.log('[initializeTokensIfMissing] Some tokens already exist, no full initialization needed');
  }
};

// Ensure token is available, refreshing if necessary
const ensureTokenAvailable = async (
  tokenType: 'frontend' | 'backend' | 'ad' = 'backend',
  msalInstance?: IPublicClientApplication,
  account?: AccountInfo,
  clientId?: string
): Promise<string | null> => {
  console.log(`[ensureTokenAvailable] Ensuring token is available for type: ${tokenType}`);
  
  // Use global MSAL state if parameters not provided
  const instance = msalInstance || getMsalInstanceSafely();
  const accounts = account ? [account] : getAccountsSafely();
  const primaryAccount = accounts[0];
  
  if (!instance || !primaryAccount) {
    console.warn(`[ensureTokenAvailable] No MSAL instance or account available for ${tokenType} token`);
    return null;
  }
  
  let token = getTokenByType(tokenType);
  
  // If token is missing, try to refresh it
  if (!token) {
    console.log(`[ensureTokenAvailable] Token missing for ${tokenType}, attempting to refresh...`);
    try {
      switch (tokenType) {
        case 'frontend':
          token = await TokenManager.refreshFrontendToken(instance, primaryAccount);
          break;
        case 'backend':
          token = await TokenManager.refreshBackendToken(instance, primaryAccount);
          break;
        case 'ad':
          if (clientId) {
            token = await TokenManager.refreshAdToken(instance, primaryAccount, clientId);
          } else {
            console.warn('[ensureTokenAvailable] Client ID required for AD token refresh');
          }
          break;
      }
      console.log(`[ensureTokenAvailable] Token refreshed successfully for ${tokenType}`);
    } catch (error) {
      console.log(`[ensureTokenAvailable] Failed to refresh token for ${tokenType}:`, error);
    }
  }
  
  return token;
};

// Refresh token based on type
const refreshTokenByType = async (
  msalInstance?: IPublicClientApplication,
  account?: AccountInfo,
  tokenType: 'frontend' | 'backend' | 'ad' = 'backend',
  clientId?: string
): Promise<void> => {
  console.log(`[refreshTokenByType] Starting token refresh for type: ${tokenType}`);
  
  // Use global MSAL state if parameters not provided
  const instance = msalInstance || getMsalInstanceSafely();
  const accounts = account ? [account] : getAccountsSafely();
  const primaryAccount = accounts[0];
  
  if (!instance || !primaryAccount) {
    throw new Error(`No MSAL instance or account available for ${tokenType} token refresh`);
  }
  
  console.log('[refreshTokenByType] Account:', { username: primaryAccount.username, homeAccountId: primaryAccount.homeAccountId });
  
  try {
    switch (tokenType) {
      case 'frontend':
        console.log('[refreshTokenByType] Refreshing frontend token...');
        await TokenManager.refreshFrontendToken(instance, primaryAccount);
        break;
      case 'backend':
        console.log('[refreshTokenByType] Refreshing backend token...');
        await TokenManager.refreshBackendToken(instance, primaryAccount);
        break;
      case 'ad':
        console.log(`[refreshTokenByType] Refreshing AD token with clientId: ${clientId}`);
        if (!clientId) throw new Error('Client ID is required for AD token refresh');
        await TokenManager.refreshAdToken(instance, primaryAccount, clientId);
        break;
      default:
        console.log('[refreshTokenByType] Refreshing default backend token...');
        await TokenManager.refreshBackendToken(instance, primaryAccount);
    }
    console.log(`[refreshTokenByType] Token refresh completed successfully for type: ${tokenType}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`[refreshTokenByType] Token refresh failed for type: ${tokenType}`, error);
    throw error;
  }
};

// Enhanced error handling with specific error messages
const handleApiError = (
  error: ApiError,
  endpoint: string
): { error: FetchBaseQueryError } => {
  // Log error for debugging (development only)
  if (process.env.NODE_ENV === 'development') {
    const logData = {
      status: error.status,
      message: error.data?.message,
      details: error.data?.details,
      endpoint,
    };
    // eslint-disable-next-line no-console
    console.error('API Error:', logData);
  }

  // Default error messages for common HTTP status codes
  const defaultMessages: Record<number, string> = {
    400: 'Bad request - please check your input',
    401: 'Authentication required - please log in',
    403: 'Access forbidden - insufficient permissions',
    404: 'Resource not found',
    409: 'Conflict - resource already exists',
    422: 'Validation error - please check your input',
    429: 'Too many requests - please try again later',
    500: 'Internal server error - please try again later',
    502: 'Service temporarily unavailable',
    503: 'Service unavailable - please try again later',
    504: 'Request timeout - please try again',
  };

  const errorMessage = error.data?.message || defaultMessages[error.status] || 'An unexpected error occurred';

  return {
    error: {
      ...error,
      data: {
        ...error.data,
        message: errorMessage,
      },
    } as FetchBaseQueryError,
  };
};

/**
 * Creates a reusable base query with automatic token refresh and error handling
 * 
 * @param config - Configuration object for the base query
 * @returns BaseQueryFn that can be used in RTK Query APIs
 * 
 * @example
 * ```typescript
 * const baseQuery = createAuthBaseQuery({
 *   baseUrl: process.env.REACT_APP_USER_API_BASE_URL!,
 *   tokenType: 'backend'
 * });
 * 
 * export const userApiSlice = createApi({
 *   baseQuery,
 *   endpoints: (builder) => ({
 *     // your endpoints
 *   })
 * });
 * ```
 */
export const createAuthBaseQuery = (config: BaseQueryConfig): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const {
    baseUrl,
    tokenType = 'backend',
    clientId,
    customHeaders = {},
    onAuthenticationFailure = defaultAuthFailureHandler,
    waitForMsal = true,
    msalTimeoutMs = 10000,
    requiresAuth = true,
    skipForbiddenRedirectForEndpoints = [],
  } = config;

  console.log('[createAuthBaseQuery] Creating base query with config:', {
    baseUrl,
    tokenType,
    clientId: clientId ? `${clientId.substring(0, 8)}...` : undefined,
    customHeadersCount: Object.keys(customHeaders).length,
    waitForMsal,
    msalTimeoutMs,
    requiresAuth,
  });

  return async (args, api, extraOptions) => {
    const endpoint = typeof args === 'string' ? args : args.url || 'unknown';
    console.log(`[createAuthBaseQuery] Making request to endpoint: ${endpoint}`);
    
    let msalInstance: IPublicClientApplication | null = null;
    let primaryAccount: AccountInfo | null = null;
    
    // Handle authentication setup
    if (requiresAuth) {
      try {
        // Wait for MSAL initialization if requested
        if (waitForMsal && !isMsalReady()) {
          console.log('[createAuthBaseQuery] Waiting for MSAL initialization...');
          const msalState = await waitForMsalInitialization(msalTimeoutMs);
          msalInstance = msalState.instance;
          primaryAccount = msalState.accounts[0] || null;
        } else {
          // Use current MSAL state
          msalInstance = getMsalInstanceSafely();
          const accounts = getAccountsSafely();
          primaryAccount = accounts[0] || null;
        }
        
        if (!msalInstance || !primaryAccount) {
          console.warn('[createAuthBaseQuery] No MSAL instance or account available');
          if (requiresAuth) {
            return {
              error: {
                status: 401,
                statusText: 'Unauthorized',
                data: { message: 'Authentication required but no valid session found' }
              } as FetchBaseQueryError
            };
          }
        } else {
          // Initialize tokens if missing
          await initializeTokensIfMissing(msalInstance, primaryAccount, clientId);
          
          // Ensure the specific token we need is available
          await ensureTokenAvailable(tokenType, msalInstance, primaryAccount, clientId);
        }
      } catch (error) {
        console.log('[createAuthBaseQuery] Failed to ensure token is available:', error);
        // Continue with request - the auth error handling below will catch it
      }
    }
    
    // Create the base query with headers
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        
        // Add authentication token
        const token = getTokenByType(tokenType);
    
        if (token) {
          const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
          headers.set('Authorization', authHeader);
        } else {
          console.log('[createAuthBaseQuery] No token available for request');
        }
        // Add standard headers only if not already set

        //Note: This has been commented to check if it resolves issues with some APIs
        // if (!headers.has('Content-Type')) {
        //   headers.set('Content-Type', 'application/json');
        // }
        if (!headers.has('Accept')) {
          headers.set('Accept', 'application/json');
        }

        // Add custom headers
        Object.entries(customHeaders).forEach(([key, value]) => {
          headers.set(key, value);
        });
        return headers;
      },
    });
    
    // First attempt with current token
    console.log('[createAuthBaseQuery] Making initial request attempt');
    let result = await baseQuery(args, api, extraOptions);

    console.log('[createAuthBaseQuery] Initial request completed with status:', result.error ? 'error' : 'success');
    if (result.error) {
      const errorDetails = result.error as FetchBaseQueryError;
      console.log('[createAuthBaseQuery] Request error details:', {
        status: errorDetails.status,
        statusText: (errorDetails as { statusText?: string }).statusText,
      });
    }

    // Handle 401 authentication errors with automatic token refresh
    if (result.error && (result.error as AuthenticationError).status === 401) {
      console.log('[createAuthBaseQuery] 401 error detected, attempting token refresh');
      
      try {
        // Use global MSAL state for token refresh
        const refreshMsalInstance = getMsalInstanceSafely();
        const refreshAccounts = getAccountsSafely();
        const refreshAccount = refreshAccounts[0];

        console.log('[createAuthBaseQuery] MSAL instance available:', !!refreshMsalInstance);
        console.log('[createAuthBaseQuery] Accounts available:', refreshAccounts.length);

        if (refreshMsalInstance && refreshAccount) {
          console.log('[createAuthBaseQuery] Proceeding with token refresh');
          
          // Refresh the token
          await refreshTokenByType(
            refreshMsalInstance,
            refreshAccount,
            tokenType,
            clientId
          );

          console.log('[createAuthBaseQuery] Token refreshed, retrying request');

          // Create new base query with refreshed token
          const retryBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
              const refreshedToken = getTokenByType(tokenType);
              if (refreshedToken) {
                const authHeader = refreshedToken.startsWith('Bearer ') ? refreshedToken : `Bearer ${refreshedToken}`;
                headers.set('Authorization', authHeader);
                console.log('2Authorization ', authHeader);
              }
               //Note: This has been commented to check if it resolves issues with some APIs
              //headers.set('Content-Type', 'application/json');
              headers.set('Accept', 'application/json');
              Object.entries(customHeaders).forEach(([key, value]) => {
                headers.set(key, value);
              });
              return headers;
            },
          });

          // Retry the original request with the new token
          console.log('[createAuthBaseQuery] Making retry request with refreshed token');
          result = await retryBaseQuery(args, api, extraOptions);
          console.log('[createAuthBaseQuery] Retry request completed with status:', result.error ? 'error' : 'success');
        } else {
          // No MSAL instance or accounts available
          console.log('[createAuthBaseQuery] No MSAL instance or accounts available, calling auth failure handler');
          onAuthenticationFailure();
          return {
            error: {
              status: 401,
              data: { message: 'Authentication failed - please log in again' },
            } as FetchBaseQueryError,
          };
        }
      } catch (refreshError) {
        // Token refresh failed
        // eslint-disable-next-line no-console
        console.error('[createAuthBaseQuery] Token refresh failed:', refreshError);
        onAuthenticationFailure();
        return {
          error: {
            status: 401,
            data: {
              message: 'Token refresh failed - please log in again',
              details: String(refreshError),
            },
          } as FetchBaseQueryError,
        };
      }
    }

    // Redirect to unauthorized page for forbidden responses, unless this specific
    // endpoint has opted out (a resource-specific 403 it wants to handle itself).
    if (result.error && result.error.status === 403) {
      if (!skipForbiddenRedirectForEndpoints.includes(api.endpoint)) {
        defaultForbiddenHandler();
      }
    }

    // Handle other types of errors
    if (result.error) {
      console.log('[createAuthBaseQuery] Handling non-401 error for endpoint:', endpoint);
      return handleApiError(result.error as ApiError, endpoint);
    }

    console.log('[createAuthBaseQuery] Request completed successfully');
    return result;
  };
};

// Convenience functions for common configurations
export const createBackendBaseQuery = (
  baseUrl: string,
  customHeaders?: Record<string, string>,
  waitForMsal = true,
  msalTimeoutMs = 10000,
  skipForbiddenRedirectForEndpoints?: string[]
) => {
  console.log('[createBackendBaseQuery] Creating backend base query for URL:', baseUrl);
  return createAuthBaseQuery({
    baseUrl,
    tokenType: 'backend',
    customHeaders,
    waitForMsal,
    msalTimeoutMs,
    requiresAuth: true,
    skipForbiddenRedirectForEndpoints,
  });
};

export const createFrontendBaseQuery = (
  baseUrl: string, 
  customHeaders?: Record<string, string>,
  waitForMsal = true,
  msalTimeoutMs = 10000
) => {
  console.log('[createFrontendBaseQuery] Creating frontend base query for URL:', baseUrl);
  return createAuthBaseQuery({
    baseUrl,
    tokenType: 'frontend',
    customHeaders,
    waitForMsal,
    msalTimeoutMs,
    requiresAuth: true,
  });
};

export const createAdBaseQuery = (
  baseUrl: string, 
  clientId: string, 
  customHeaders?: Record<string, string>,
  waitForMsal = true,
  msalTimeoutMs = 10000
) => {
  console.log('[createAdBaseQuery] Creating AD base query for URL:', baseUrl, 'with clientId:', `${clientId.substring(0, 8)}...`);
  return createAuthBaseQuery({
    baseUrl,
    tokenType: 'ad',
    clientId,
    customHeaders,
    waitForMsal,
    msalTimeoutMs,
    requiresAuth: true,
  });
};

/**
 * Wraps a base query so that the user's country (resolved from Entra ID /
 * Microsoft Graph on first access) is sent as the `country` request header.
 *
 * The country is resolved here — after MSAL is ready and inside the async base
 * query — rather than in an endpoint's `query()`, because `query()` is evaluated
 * before the request is issued and would race with the Graph fetch, yielding a
 * blank header on the very first call.
 *
 * @param baseQuery - The underlying base query to delegate to.
 * @param shouldApply - Optional predicate on the request URL; when provided, the
 *   header is only attached to matching requests and all others pass through
 *   unchanged. Omit to attach the header to every request.
 * @param logLabel - Label used when logging a non-fatal country-resolution failure.
 */
export const withCountryHeader = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  shouldApply?: (url: string) => boolean,
  logLabel = 'api'
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url;

  if (shouldApply && !shouldApply(url)) {
    return baseQuery(args, api, extraOptions);
  }

  // Ensure the country is fetched the first time before building the request
  if (!TokenManager.hasCountry()) {
    try {
      if (!isMsalReady()) {
        await waitForMsalInitialization();
      }
      const instance = getMsalInstanceSafely();
      const account = getAccountsSafely()[0];
      if (instance && account) {
        // fetchUserCountry needs a Graph (frontend) token; make sure one exists
        if (!TokenManager.getFrontendToken()) {
          await TokenManager.refreshFrontendToken(instance, account);
        }
        await TokenManager.fetchUserCountry(instance, account);
      }
    } catch (error) {
      // Non-fatal: proceed without country rather than blocking the request
      console.warn(`[${logLabel}] Failed to resolve user country:`, error);
    }
  }

  const country = TokenManager.getCountry();
  const argsWithCountry: FetchArgs =
    typeof args === 'string'
      ? { url: args, headers: { country } }
      : { ...args, headers: { ...(args.headers as Record<string, string>), country } };

  return baseQuery(argsWithCountry, api, extraOptions);
};

// Error transform utility for consistent error handling in endpoints
export const transformApiError = (response: FetchBaseQueryError) => {
  const errorData = response.data as ErrorResponse;
  return {
    status: response.status,
    message: errorData?.message || 'An error occurred',
    originalError: response,
  };
};